/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {ActionTree} from 'vuex';
import {RootState} from '@/store/types';
import {AxiosError, AxiosResponse} from 'axios';
import {auth} from '@/services/http';
import {
    PkceRefreshRequestInterface,
    PkceTokenRequestInterface,
    PkceTokenResponseInterface,
} from '@/services/pkce/types';
import {UserDetailsInterface, UserSessionStateInterface} from '@/store/modules/user-session/types';
import {state} from '@/store/modules/user-session';

/**
 * Actions file for user session operations.
 *
 * Actions are functions that can be exported and called upon potentially in an asynchronous manner.
 * To add an action export a named const function. e.g. export const fnName = ({ commit }, {id, name}) => { ... }
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const actions: ActionTree<UserSessionStateInterface, RootState> = {
    /**
     * Authenticate a user.
     *
     * @param {any} commit
     * @param {PkceTokenRequestInterface} params
     *
     * @returns {Promise<PkceTokenResponseInterface>}
     */
    authenticate({commit}: {commit: any}, params: PkceTokenRequestInterface): Promise<PkceTokenResponseInterface> {
        const formData: FormData = Object.keys(params).reduce((reducer: FormData, key: string): any => {
            reducer.append(key, params[key]);
            return reducer;
        }, new FormData());
        return auth.axios.post('token', formData, {})
            .then((response: AxiosResponse<PkceTokenResponseInterface>) => {
                commit('authenticate', {
                    token: response.data.access_token,
                    refresh: response.data.refresh_token,
                    expiry: response.data.expires_in,
                });
                return response.data;
            });
    },

    /**
     * Refresh a user token.
     *
     * @param {any} commit
     * @param {PkceTokenRequestInterface} params
     *
     * @returns {Promise<PkceTokenResponseInterface>}
     */
    refresh({commit}: {commit: any}, params: PkceRefreshRequestInterface): Promise<PkceTokenResponseInterface> {
        const formData: FormData = Object.keys(params).reduce((reducer: FormData, key: string): any => {
            reducer.append(key, params[key]);
            return reducer;
        }, new FormData());
        return auth.axios.post('token', formData, {})
            .then((response: AxiosResponse<PkceTokenResponseInterface>) => {
                commit('authenticate', {
                    token: response.data.access_token,
                    refresh: response.data.refresh_token,
                    expiry: response.data.expires_in,
                });
                return response.data;
            });
    },

    /**
     * Update the session verifier for a new request.
     *
     * @param {any} commit
     * @param {string} verifier
     *
     * @returns {Promise<any>}
     */
    updateVerifier({commit}: {commit: any}, verifier: string): void {
        commit('authenticate', {verifier});
    },

    /**
     * Logout of the user session.
     *
     * As we are using JWT tokens we just need to remove the reference to the token in the localStorage.
     *
     * @param {Promise<AxiosResponse>} commit
     */
    logout({commit}: {commit: any}): void {
        commit('logout');
    },

    /**
     * Decode to get the user data
     *
     * @param {any} commit
     * @param {string} token
     */
    decode({commit}: {commit: any}, token: string): Promise<void> {
        return auth.axios.get('decode', {
            headers: {
                Authorization: state.session.token,
            },
        }).then((response: AxiosResponse) => {

            const data: any = response.data.usr;

            const userDetails: UserDetailsInterface = {
                userId: data.customer_id,
            };

            commit('authenticate', {
                token: state.session.token,
                refresh: '',
                expiry: response.data.exp,
                verifier: '',
            });

            commit('userDetails', userDetails);

        }, (err: AxiosError) => {
            commit('userDetailsError');
        });
    },

    /**
     * Update the current view name.
     *
     * @param {any} commit
     * @param {{name: string, param?: any} | undefined} view
     *
     * @returns {void}
     */
    updateCurrentView({commit}: {commit: any}, view: {name: string, param?: any} | undefined): void {
        commit('updateCurrentView', view);
    },
};
